import pickBy from 'lodash/pickBy';
import { sanitizeHtml } from './safelyRender';
import { translateAccents } from '../utils/translateCharacterAccents';

export const getCategoryIdsFromPathname = (pathname = '') => {
    if (pathname === '/catalog/search') return '0';
    const categoryIds = pathname
        .split('/')
        .filter(x => Number(x))
        .join(',');
    return categoryIds;
};

export const getIdFromPath = path => {
    if (!path) return 0;

    const pathString = `${path}`;
    const cleaned = pathString.replace(
        /^\/?(catalog(_gallery)?|item|auctioneer|catinfot|console|checkout|terms)\/?/,
        ''
    );
    let urlFrags = cleaned.split('_')[0];

    if (urlFrags.includes('/page')) {
        urlFrags = urlFrags.split('/')[0];
    }

    const idMatches = urlFrags.match(/^(\d+)\/?$/);
    return idMatches ? Number(idMatches[1]) || 0 : 0;
};

// https://stackoverflow.com/a/1714899/148256
// Returns a string from the object
export const toQueryString = obj => {
    const arr = [];
    for (const p in obj) {
        // eslint-disable-next-line no-prototype-builtins
        if (obj.hasOwnProperty(p)) {
            arr.push(`${encodeURIComponent(p)}=${encodeURIComponent(obj[p])}`);
        }
    }
    arr.sort();
    return arr.join('&');
};

export const sellerIdFromPath = path => {
    if (!path) return 0;
    let sellerId = Number(path);

    if (!sellerId) {
        sellerId = Number(path.split('-')[0]);
    }
    return sellerId;
};

export const logoUrl = 'https://p1.liveauctioneers.com/dist/images/logo_og.jpg?auto=webp&format=pjpg';
export const signupLadySmallUrl = 'https://p1.liveauctioneers.com/dist/images/SignupLadySmall.png?auto=webp';
export const mobileLogo = 'https://p1.liveauctioneers.com/dist/images/logo-1024.png';
export const enableBrowserNotificationsUrl =
    'https://help.liveauctioneers.com/article/359-how-do-i-enable-browser-notifications';

export const urlSafeTitle = (title = '') => {
    let cleanedAndDecodedHtml = sanitizeHtml(title);
    cleanedAndDecodedHtml = translateAccents(cleanedAndDecodedHtml);
    const safe = cleanedAndDecodedHtml
        .trim()
        .toLowerCase()
        // & to and
        .replace(/&/g, 'and')
        // Replace , * ; : ' " to hyphen
        .replace(/[,*;:'"]+/g, '-')
        // Replace `- -` to single hyphen
        .replace(/-*\s+-*/g, '-')
        // Remove special characters
        .replace(/[^a-zA-Z0-9-_-]/g, '')
        // many hyphen to one
        .replace(/--+/g, '-')
        // Remove hyphen if it is the last character
        .replace(/-+$/, '');
    const urlEncoded = encodeURIComponent(safe).toLowerCase(); // lower case it again since the URI encoded values can be upper case
    return urlEncoded;
};

export const setQueryParams = (base, params = {}) => {
    let cleanedBase = base.replace(/\?.*/, '');
    const queryString = toQueryString(params);
    return queryString === '' ? cleanedBase : `${cleanedBase}?${queryString}`;
};

const buildSearchUrl = (base, params) => {
    const queryString = toQueryString(params);
    return queryString === '' ? base : `${base}?${queryString}`;
};

export const getTodayUrl = () => '/trending/';

const getAuctionCalendarBaseUrl = () => '/catalog/search/';

export const getAuctionCalendarUrlWithParams = params => {
    const base = getAuctionCalendarBaseUrl();
    return buildSearchUrl(base, params);
};

const getSearchBaseUrl = () => '/search/';
export const getUrlWithSearchParams = (params, base) => {
    return buildSearchUrl(base, params);
};
export const getSearchUrlWithParams = params => {
    const base = getSearchBaseUrl();
    return getUrlWithSearchParams(params, base);
};

export const getCategoryUrlWithParams = (base, params) => {
    return buildSearchUrl(base, params);
};

export const getCategoryFacetLink = (base, params) => {
    base = base === '' || base === undefined ? '/c/' : `/c/${base}/`;
    return buildSearchUrl(base, params);
};

//keywords: string could just be 'rings' or could be 'paintings/oil'
//categoryId: string. Could be '96' or could be '10,25830'(which is a subcategory)
export const getCategoryPageUrl = (keywords, categoryId, params) => {
    const url = `/c/${keywords}/${categoryId.replace(',', '/')}/`;
    return buildSearchUrl(url, params);
};

export const getCombinedCategoryPageUrl = (firstCategory, secondCategory) => {
    const url = `/c/${urlSafeTitle(firstCategory.name)}/${urlSafeTitle(secondCategory.name)}/${firstCategory.id}/${
        secondCategory.id
    }/`;
    return buildSearchUrl(url);
};

export const getAuctionResultsUrl = () => '/auction-results';
export const getAuctioneerDirectoryUrl = () => '/auctioneer/directory/';
export const getCatalogUrl = (id, title) => `/catalog/${id}_${urlSafeTitle(title)}/`;
export const getCategoryLandingPageBaseUrl = () => '/c/';
export const getCatalogTermsUrl = (id, title) => `/terms/${id}_${urlSafeTitle(title)}`;
export const getCheckoutConfirmationUrl = (sellerId, itemId) => `/confirmation/${sellerId}/${itemId}`;
export const getCheckoutUrl = catalogId => `/checkout/${catalogId}`;
export const getCodexInfoUrl = () => 'https://www.liveauctioneers.com/pages/codexprotocol/';
export const getConsignmentsUrl = () => '/consignments';
export const getCookiePolicyUrl = () => '/cookie-policy';
export const getDisputeHelpUrl = () => 'https://help.liveauctioneers.com/article/163-how-to-handle-a-dispute';
export const getFollowedSellersUrl = () => '/dashboard/followed-auctioneers';
export const getHomeUrl = () => '/';
export const getIntellectualPropertyUrl = () => '/intellectualproperty';
export const getItemUrl = (id, title) => `/item/${id}_${urlSafeTitle(title)}`;
export const getLiveBiddingUrl = catalogId => `/console/${catalogId}`;
export const getMessagesUrl = () => '/dashboard/messages';
export const getMyAuctionsUrl = () => '/dashboard/my-auctions';
export const getMyBidsUrl = () => '/dashboard/my-bids';
export const getNearMeUrl = () => '/find-auctions-near-me';
export const getNewCatalogConsoleUrl = (id, title) => `/console/${id}_${urlSafeTitle(title)}/`;
export const getNewsUrl = () => 'https://www.liveauctioneers.com/news/';
export const getNotFoundUrl = () => '/not-found';
export const getRecommendedItemsUrl = () => '/recommended';
export const getRecentItemsUrl = () => '/recent';
export const getReviewUrl = catalogId => `/review/${catalogId}`;
export const getPriceGuideBaseUrl = () => '/price-guide/';
export const getPriceGuideUrl = (name, id) => `${getPriceGuideBaseUrl()}${name}/${id}/`;
export const getPrivacyUrl = () => '/privacy';
export const getProfileUrl = provider => (provider ? `/dashboard/profile?provider=${provider}` : '/dashboard/profile');
export const getSavedItemsUrl = () => '/dashboard/saved-items';
export const getSavedSearchesUrl = append => `/dashboard/search-and-alerts/${append ? `${append}/` : ''}`;
export const getSellPageUrl = () => 'https://www.liveauctioneers.com/pages/sell/';
export const getSellerUrl = (id, title) => `/auctioneer/${id}/${urlSafeTitle(title)}/`;
export const getSellerReviewsUrl = (id, title, filter = '') =>
    filter
        ? `/auctioneer/${id}/${urlSafeTitle(title)}/reviews?filter=${filter}`
        : `/auctioneer/${id}/${urlSafeTitle(title)}/reviews`;
export const getSupportUrl = () => 'https://help.liveauctioneers.com';
export const getTerminologyUrl = () => '/terminology';
export const getTermsAndConditionsUrl = () => '/termsandconditions';
export const getWonItemsPageUrl = () => '/dashboard/won-items';

export const addImageQueryParams = (url = '', width, height) => {
    if (!url) return '';
    const correctImageUrl = url.replace('http://', 'https://');
    const size = [];
    if (width) size.push(`width=${width}&`);
    if (height) size.push(`height=${height}&`);
    return `${correctImageUrl}?${size.join('')}auto=webp&format=pjpg`;
};

export const getAuctioneerLogoUrl = (auctioneer, width = 110) => {
    return `https://images.liveauctioneers.com/houses/logos/lg/${auctioneer}_large.jpg?auto=webp&format=pjpg&width=${width}`;
};

export const getAllUpcomingAuctionsUrl = () => getAuctionCalendarUrlWithParams();

export const toQString = obj => {
    const arr = [];
    for (const p in obj) {
        // eslint-disable-next-line no-prototype-builtins
        if (obj.hasOwnProperty(p)) {
            arr.push(`${encodeURIComponent(p)}=${obj[p]}`);
        }
    }
    arr.sort();
    const joined = arr.join('&');
    return joined ? `?${joined}` : '';
};

export const getImageUrl = ({
    blur,
    catalogId,
    crop,
    height,
    imageNumber = 1,
    imageVersion = 1,
    itemId,
    quality,
    sellerId,
    width,
}) => {
    if (!sellerId || !catalogId || !itemId || !imageNumber) {
        return '';
    }

    const query = pickBy({
        auto: 'webp',
        blur,
        crop,
        format: 'pjpg',
        height,
        quality,
        version: imageVersion,
        width,
    });

    return `https://p1.liveauctioneers.com/${sellerId}/${catalogId}/${itemId}_${imageNumber}_x.jpg${toQString(query)}`;
};

export const getThumbnailItemImageUrl = (sellerId, catalogId, itemId, imageNumber, imageVersion = 1, width = 128) =>
    getImageUrl({ catalogId, imageNumber, imageVersion, itemId, sellerId, width });

export const getPlaceholderItemImageUrl = (sellerId, catalogId, itemId, imageNumber, imageVersion = 1) =>
    getImageUrl({ catalogId, imageNumber, imageVersion, itemId, quality: 10, sellerId, width: 612 });

export const getMediumItemImageUrl = (sellerId, catalogId, itemId, imageNumber, imageVersion = 1) =>
    getImageUrl({ catalogId, imageNumber, imageVersion, itemId, sellerId, width: 512 });

export const getLargeItemImageUrl = (sellerId, catalogId, itemId, imageNumber, imageVersion = 1) =>
    getImageUrl({ catalogId, imageNumber, imageVersion, itemId, sellerId, width: 768 });

export const getXLargeItemImageUrl = (sellerId, catalogId, itemId, imageNumber, imageVersion = 1) =>
    getImageUrl({ catalogId, imageNumber, imageVersion, itemId, sellerId });

// export const getItemImageUrl = (
//     sellerId,
//     catalogId,
//     itemId,
//     imageNumber,
//     imageVersion = 1,
//     width,
//     limitQuality = true,
//     crop = ''
// ) => {
//     const quality = limitQuality ? 50 : '';

//     return getImageUrl({ catalogId, crop, imageNumber, imageVersion, itemId, quality, sellerId, width });
// };

export const getCuratorImageUrl = (baseUrl, curatorId, curatorImageFileName) =>
    `${baseUrl}/${curatorId}/${encodeURIComponent(curatorImageFileName)}`;

export const getItemCardImageUrl = (sellerId, catalogId, itemId, imageNumber, imageVersion) =>
    getImageUrl({ catalogId, imageNumber, imageVersion, itemId, sellerId, width: 280 });

export const getMegaNavImageUrlLarge = category =>
    `https://p1.liveauctioneers.com/dist/images/desktop-${category}.jpg?auto=webp&format=pjpg&height=382`;

export const getMegaNavImageUrlSmall = category =>
    `https://p1.liveauctioneers.com/dist/images/tablet-${category}.jpg?auto=webp&format=pjpg&height=288`;

export const getDeskSupportLink = () => 'https://help.liveauctioneers.com/';

export const getAdQuestionUrl = () => 'https://www.surveymonkey.com/r/KFCCVSX';

export const getSearchPageMostPopular = () => getSearchUrlWithParams({ page: 1, sort: '-popularity' });
export const getSearchPageMostBids = () => getSearchUrlWithParams({ page: 1, sort: '-bidCount' });

const categoryLandingIds = {
    art: 1,
    asian: 158,
    collectibles: 2,
    fashion: 3,
    furniture: 4,
    'home-garden': 5,
    jewelry: 6,
};

export const getCategoryLandingPageUrlFromCategoryName = category => {
    const categoryId = categoryLandingIds[category];
    return categoryId
        ? `${getCategoryLandingPageBaseUrl()}${category}/${categoryId}/`
        : getCategoryLandingPageBaseUrl();
};

export const getCategoryPageUrlFromObject = category => {
    let url = getCategoryLandingPageBaseUrl();
    if (!category || !category.l1CategoryId) {
        return url;
    }
    url += `${category.l1CategoryUrl}/`;
    if (typeof category.l2CategoryId !== undefined && category.l2CategoryId) {
        url += `${category.l2CategoryUrl}/${category.l1CategoryId}/${category.l2CategoryId}/`;
    } else {
        url += `${category.l1CategoryId}/`;
    }
    return url;
};

export const separateURL = url => {
    const match = url.match(/^(https?:)\/\/(([^:/?#]*)(?::([0-9]+))?)([/]{0,1}[^?#]*)(\?[^#]*|)(#.*|)$/);
    if (match === null) {
        return {
            host: '',
            path: '',
        };
    }
    return {
        host: match[2],
        path: match[5],
    };
};

export const getSearchAuctioneerUrl = sellerId =>
    getSearchUrlWithParams({
        house_id: `${sellerId}`,
        page: 1,
        sort: '-popularity',
    });

export const getSearchAuctioneerSoldUrl = sellerId =>
    getSearchUrlWithParams({
        house_id: `${sellerId}`,
        page: 1,
        status: 'archive',
    });

export const getUpcomingTitleSearchParams = (title = '', sellerId = 0) => {
    const keyword = title
        .split(/\s+/g)
        .map(word => `${word}`)
        .join(' OR ')
        .replace(/"/g, '\\"');
    const params = { keyword, status: ['online', 'live', 'upcoming'] };
    if (sellerId) {
        params.house_id = sellerId.toString();
    }
    return params;
};

export const getSimilarItemsUrl = (title = '', sellerId = 0) => {
    const params = getUpcomingTitleSearchParams(title, sellerId);
    return getSearchUrlWithParams(params);
};

export const getItemCardImage = (sellerId, catalogId, height, itemId, imageNumber = 1, imageVersion = 1) => {
    return getImageUrl({
        catalogId,
        height,
        imageNumber,
        imageVersion,
        itemId,
        sellerId,
    });
};

export const getCoverLotUrls = (
    catalogId,
    coverLots,
    sellerId,
    firstImageHeight,
    secondImageHeight,
    thirdImageHeight,
    catalogUrl
) => {
    const { results } = coverLots || {};
    const [item1 = {}, item2 = {}, item3 = {}] = results;

    const coverLotImgAlt1 = item1.title;
    const coverLotImgAlt2 = item2.title;
    const coverLotImgAlt3 = item3.title;

    const coverLotImgSrc1 = getImageUrl({
        catalogId,
        height: firstImageHeight,
        imageNumber: item1.imageNumber || 1,
        imageVersion: item1.imageVersion,
        itemId: item1.itemId,
        sellerId,
    });

    const coverLotImgSrc2 = getImageUrl({
        catalogId,
        height: secondImageHeight,
        imageNumber: item2.imageNumber || 1,
        imageVersion: item2.imageVersion,
        itemId: item2.itemId,
        sellerId,
    });

    const coverLotImgSrc3 = getImageUrl({
        catalogId,
        height: thirdImageHeight,
        imageNumber: item3.imageNumber || 1,
        imageVersion: item3.imageVersion,
        itemId: item3.itemId,
        sellerId,
    });

    let coverLotImgHref1, coverLotImgHref2, coverLotImgHref3;
    if (catalogUrl) {
        coverLotImgHref1 = catalogUrl;
        coverLotImgHref2 = catalogUrl;
        coverLotImgHref3 = catalogUrl;
    } else {
        coverLotImgHref1 = getItemUrl(item1.itemId, item1.title);
        coverLotImgHref2 = getItemUrl(item2.itemId, item2.title);
        coverLotImgHref3 = getItemUrl(item3.itemId, item3.title);
    }

    return {
        primary: {
            imageAlt: coverLotImgAlt1,
            imageHref: coverLotImgHref1,
            imageSrc: coverLotImgSrc1,
        },
        secondary1: {
            imageAlt: coverLotImgAlt2,
            imageHref: coverLotImgHref2,
            imageSrc: coverLotImgSrc2,
        },
        secondary2: {
            imageAlt: coverLotImgAlt3,
            imageHref: coverLotImgHref3,
            imageSrc: coverLotImgSrc3,
        },
    };
};

export const getWebviewsUrl = (path, deployment) => {
    let domain = `https://webviews${deployment === 'prod' ? '' : `-${deployment}`}.liveauctioneers.com`;
    return `${domain}/${path}`;
};

export const getPayrixAddCreditCardUrl = (deployment, address, customerId) => {
    let url = getWebviewsUrl('addcreditcard/3', deployment);
    let queryParam = '';
    if (address) {
        queryParam = `address1=${encodeURIComponent(address.address1)}&address2=${encodeURIComponent(
            address.address2
        )}&state=${encodeURIComponent(address.state)}&countryCode=${encodeURIComponent(
            address.countryCode
        )}&city=${encodeURIComponent(address.city)}&postalCode=${encodeURIComponent(address.postalCode)}`;
    }
    if (customerId) {
        queryParam = `${queryParam}${queryParam ? '&' : ''}customerId=${encodeURIComponent(customerId)}`;
    }
    if (queryParam) {
        url = `${url}?${queryParam}`;
    }
    return url;
};

export const getPayrixTermsUrl = () => 'https://portal.payrix.com/terms';

export const getTaxCertCaptureUrl = (deployment, token, shipZone) => {
    let url = getWebviewsUrl('taxcertcapture', deployment);
    let queryParam = '';
    if (token && shipZone) {
        queryParam = `token=${token}&shipZone=${shipZone}`;
    }
    if (queryParam) {
        url = `${url}?${queryParam}`;
    }

    return url;
};

export const getCertCapturePageUrl = shipZone => {
    const url = '/certcapture';
    let queryParam = '';
    if (shipZone) {
        queryParam = `?shipZone=${shipZone}`;
    }
    return `${url}${queryParam}`;
};
